import { Loader, MultiSelect, Radio, Stack } from '@mantine/core';
import { SearchDatabaseOptions } from '@scaleup/models';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import Image from 'next/image';
import { useEffect } from 'react';
import { useGetUserQuery } from '../../../api';
import { SUPPLIER_PREFERENCES } from '../../../constants';
import { LDFeatureFlags, useLDFeatureFlag } from '../../../hooks';
import { Button } from '../../../ui';
import { useSupplierRequestWizardContext } from './context';
export const Preferences = () => {
  const {
    flow,
    setCurrentStep,
    handleChangeValue,
    saveDraftLoading,
    saveSupplierRequestDraft,
  } = useSupplierRequestWizardContext();
  const goBack = flow === 'upload' ? 'INITIAL' : 'REQUIREMENTS';
  const { data: user, isLoading: userLoading } = useGetUserQuery();
  const shouldShowDatabaseOptions = Boolean(
    process.env.NEXT_PUBLIC_CUSTOMER_DATABASE_SEARCH_AVAILABLE === 'true'
  );

  useEffect(() => {
    // set default value when enabled
    if (shouldShowDatabaseOptions) {
      handleChangeValue(
        'databasePreference',
        SearchDatabaseOptions.USE_COMPANY_DATABASE_ONLY
      );
    }
  }, []);

  const handleContinue = () => {
    setCurrentStep('REVIEW');
  };

  if (userLoading) {
    return <Loader />;
  }

  return (
    <div className="px-32">
      <div className="flex gap-8">
        <div className="flex flex-col gap-4 flex-1 max-w-[3/5]">
          <div>
            <p className="text-xl font-bold text-text-primary">
              Preferred Diversity Categories
            </p>
            <p className="pt-2 pb-4 text-base text-text-secondary">
              Select your preferred categories that aligns with your company’s
              diversity goals.
            </p>

            <MultiSelect
              data={SUPPLIER_PREFERENCES}
              className="w-3/5"
              onChange={(vals: string[]) =>
                handleChangeValue('supplierPreference', vals)
              }
            />
          </div>

          {shouldShowDatabaseOptions && (
            <div className="py-4">
              <p className="text-xl font-bold text-text-primary">
                Preferred Database
              </p>
              <p className="pt-2 pb-4 text-base text-text-secondary">
                Choose the database you would like to use for your supplier
                search.
              </p>

              <Radio.Group
                defaultValue="USE_COMPANY_DATABASE_ONLY"
                onChange={(val: string) => {
                  handleChangeValue('databasePreference', val);
                }}
              >
                <Stack>
                  <Radio
                    value={SearchDatabaseOptions.USE_COMPANY_DATABASE_ONLY}
                    label={`${user?.org?.orgName || 'Your company'}'s database`}
                  />
                  <Radio
                    value={
                      SearchDatabaseOptions.USE_COMPANY_AND_SCALEUP_DATABASE
                    }
                    label={`${user?.org?.orgName || 'Your company'}'s database and ScaleUp's database`}
                  />
                </Stack>
              </Radio.Group>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Image
            src="/procurement.png"
            alt="Supplier Request Image"
            priority
            width={512}
            height={584}
            style={{ objectFit: 'contain' }}
          />
        </div>
      </div>
      <div className="flex justify-between pt-8">
        <div>
          <Button
            text="Back"
            variant="secondary"
            prefixIcon={<IconArrowLeft />}
            onClick={() => setCurrentStep(goBack)}
          />
        </div>

        <div className="flex gap-4">
          {useLDFeatureFlag(
            LDFeatureFlags.SUPPLIER_REQUEST_SAVE_AS_DRAFT,
            false
          ) && (
            <Button
              text="Save as Draft"
              loading={saveDraftLoading}
              prefixIcon="/icons/draft.svg"
              variant={'secondary'}
              theme="light"
              onClick={saveSupplierRequestDraft}
            />
          )}

          <Button
            text="Continue"
            variant="primary"
            suffixIcon={<IconArrowRight />}
            onClick={() => setCurrentStep('REVIEW')}
          />
        </div>
      </div>
    </div>
  );
};
